import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { Column, Product, Price, VolumePrice } from '@/modelos/precios/PriceTypes';
import { listaPreciosService, type ListaPreciosColumnaDto } from '@/services/api/lista-precios.service';

const mapColumnaDto = (dto: ListaPreciosColumnaDto): Column => ({
  id: dto.codigo,
  uuid: dto.id,
  name: dto.nombre,
  mode: dto.modo as 'fixed' | 'volume',
  visible: dto.esVisible,
  isVisibleInTable: dto.esVisibleTabla,
  isBase: dto.esBase,
  order: dto.orden,
  kind: dto.kind as Column['kind'],
  globalRuleType: (dto.reglaGlobalTipo as Column['globalRuleType']) ?? undefined,
  globalRuleValue: dto.reglaGlobalValor ?? null,
  usarEnPuntoVenta: dto.esUsarEnPos,
  usarEnComprobantes: dto.esUsarEnComprobante,
  esPredeterminadoPos: dto.esPredeterminadoPos,
  esPredeterminadoComprobante: dto.esPredeterminadoComprobante,
});

export function usePriceCalculator() {
  const [columns, setColumns] = useState<Column[]>([]);
  const [productos, setProductos] = useState<Product[]>([]);
  const cargadoRef = useRef(false);

  useEffect(() => {
    if (cargadoRef.current) return;
    cargadoRef.current = true;

    void Promise.all([
      listaPreciosService.getColumnas(),
      listaPreciosService.getPrecios(),
    ])
      .then(([colRes, preciosRes]) => {
        if (!colRes.ok) {
          cargadoRef.current = false;
          return;
        }

        const cols = colRes.data.map(mapColumnaDto).sort((a, b) => a.order - b.order);
        setColumns(cols);

        if (!preciosRes.ok) return;

        const colMap = new Map<string, string>();
        cols.forEach(c => { if (c.uuid) colMap.set(c.uuid, c.id); });

        const bySkuMap = new Map<string, Product>();
        for (const dto of preciosRes.data) {
          const columnCodigo = colMap.get(dto.columnaId);
          if (!columnCodigo) continue;

          let product = bySkuMap.get(dto.sku);
          if (!product) {
            product = { sku: dto.sku, name: dto.sku, prices: {}, activeUnitCode: '' };
            bySkuMap.set(dto.sku, product);
          }
          if (!product.prices[columnCodigo]) product.prices[columnCodigo] = {};

          const price: Price = dto.tipo === 'fixed'
            ? { type: 'fixed', value: dto.valor ?? 0, validFrom: dto.vigenteDesde, validUntil: dto.vigenteHasta }
            : { type: 'volume', ranges: dto.rangos ? (JSON.parse(dto.rangos) as VolumePrice['ranges']) : [], validFrom: dto.vigenteDesde, validUntil: dto.vigenteHasta };

          product.prices[columnCodigo][dto.unidadCodigo] = price;
        }

        setProductos(Array.from(bySkuMap.values()));
      })
      .catch(() => {
        cargadoRef.current = false;
      });
  }, []);

  const baseColumn = useMemo(() => columns.find(c => c.isBase), [columns]);

  const productosMap = useMemo(() => {
    const map = new Map<string, Product>();
    productos.forEach(p => map.set(p.sku, p));
    return map;
  }, [productos]);

  // All prices are loaded at startup — this is intentionally a no-op
  const ensurePricesForSku = useCallback((_sku: string) => {}, []);

  const registerLocalFixedPrice = useCallback((input: {
    sku: string;
    columnCode: string;
    unitCode: string;
    value: number;
    vigenteDesde: string;
    vigenteHasta: string;
  }) => {
    const price: Price = {
      type: 'fixed',
      value: input.value,
      validFrom: input.vigenteDesde,
      validUntil: input.vigenteHasta,
    };

    setProductos(prev => {
      const index = prev.findIndex(p => p.sku === input.sku);
      if (index < 0) {
        return [
          ...prev,
          {
            sku: input.sku,
            name: input.sku,
            prices: { [input.columnCode]: { [input.unitCode]: price } },
            activeUnitCode: input.unitCode,
          },
        ];
      }

      const existing = prev[index];
      const next = [...prev];
      next[index] = {
        ...existing,
        prices: {
          ...existing.prices,
          [input.columnCode]: {
            ...(existing.prices[input.columnCode] ?? {}),
            [input.unitCode]: price,
          },
        },
      };
      return next;
    });
  }, []);

  const getUnitPrice = (sku: string, columnId?: string, unitCode?: string): number | null => {
    const targetColumnId = columnId ?? baseColumn?.id;
    if (!targetColumnId) return null;
    const product = productosMap.get(sku);
    if (!product) return null;
    const unitPrices = product.prices[targetColumnId];
    if (!unitPrices) return null;
    const resolvedUnit = unitCode || product.activeUnitCode || Object.keys(unitPrices)[0];
    if (!resolvedUnit) return null;
    const price: Price | undefined = unitPrices[resolvedUnit];
    if (!price) return null;
    if (price.type === 'fixed') return price.value;
    if (price.ranges.length === 0) return null;
    return [...price.ranges].sort((a, b) => a.minQuantity - b.minQuantity)[0].price;
  };

  return { columns, productos, baseColumn, getUnitPrice, ensurePricesForSku, registerLocalFixedPrice };
}
